import { useContext, useEffect } from "react"

import usePreventUnload from "@p-ban/common/src/hooks/usePreventUnload"

import type { EstimateErrors } from "../../../../../api/estimate/csComEst.d"
import csComEstApi from "../../../../../api/estimate/csComEstApi"
import useEstimateQuery from "../../../../../api/estimate/useEstimateQuery"
import loginApi from "../../../../../api/login/loginApi"
import useNextRouter from "../../../../../routes/useNextRouter"
import notify from "../../../../../utils/notify"
import MANUFACTURE_COURSE, {
  getManufactureCourseByValue,
  type ManufactureCourse,
} from "../../../constant/manufactureCourse"
import MOUNT_COURSE, { getManufactureMountByValue } from "../../../constant/mountCourse"
import { SaveEstimateContext } from "../../../save/SaveEstimateContext"
import isDeliveryZeroAvailable from "../../../service/deliveryZeroAvailabilityChecker"
import parseResult from "../../../service/parseResult"
import type { EstimateParams } from "../../../types/params"
import type { ManufactureResult, MountResult, ResultsPerCourse } from "../../../types/parsedResult"
import OneClickEstimateContext from "../../OneClickEstimateContext"
import MODAL_ID from "../../constant/modalId"
import { useModal } from "../../layout/modal/useModal"
import sendMountQuickSelect from "../../../../../api/estimate/csMountQuickSelectApi"

/**
 * 実装短納期の活性/非活性を判定する
 *
 * @param manufacture - 製造コースごとの見積結果
 * @param mount - 実装の見積結果
 * @param manufactureCourseKey - 選択された製造コースのキー
 * @param hasProcurement - 部品調達サービスの有無
 * @returns true: 非活性, false: 活性
 *
 * @description
 * ## 非活性になる条件
 * 1. 部品調達サービスがある場合
 * 2. ノーマルコースを選択している場合
 * 3. 選択したコースより速いコースに通常見積（個別見積でない）のコースが存在する場合
 */
const shouldDisableMountExpress = (
  manufacture: ResultsPerCourse<ManufactureResult> | undefined,
  mount: MountResult | undefined,
  manufactureCourseKey: ManufactureCourse["key"],
  hasProcurement: boolean,
): boolean => {
  if (!mount) return true
  if (hasProcurement) return true
  if (manufactureCourseKey === MANUFACTURE_COURSE.normal.key) return true
  if (manufactureCourseKey === MANUFACTURE_COURSE.massProduction.key) return false
  if (!manufacture) return true

  const courseOrder: ManufactureCourse["key"][] = ["ultraQuick", "superQuick", "quick", "normal", "massProduction"]
  const selectedCourseIndex = courseOrder.indexOf(manufactureCourseKey)

  for (let i = 0; i < selectedCourseIndex; i++) {
    const upperCourseKey = courseOrder[i] as ManufactureCourse["key"]
    const upperCourse = manufacture[upperCourseKey]

    if (!upperCourse.isIndividual) {
      return true
    }
  }

  return false
}

const useEstimateResult = (
  params: EstimateParams,
  setErrors: React.Dispatch<React.SetStateAction<EstimateErrors>>,
  openLoginModal: (options: { onMount: () => void; onLogin: () => void; shouldRedirect: boolean }) => void,
  enabled: boolean,
) => {
  const { manufactureCourse, setManufactureCourse, MountCourse, setManufactureMount, setMountQuickFlg } =
    useContext(OneClickEstimateContext)
  const { setEstimateSuccessResult } = useContext(SaveEstimateContext)
  const { open: openDeliveryZeroModal } = useModal(MODAL_ID.DELIVERY_ZERO)

  const onChangeManufactureCourse = (e: React.ChangeEvent<HTMLInputElement>) =>
    setManufactureCourse(getManufactureCourseByValue(e.target.value))

  const handleMountChange = (value: string) => {
    setManufactureMount(getManufactureMountByValue(value))
    setMountQuickFlg(getManufactureMountByValue(value) === MOUNT_COURSE.mountExpress)

    // 実装短納期を選択した場合、実装短納期選択処理を実行する
    if (getManufactureMountByValue(value) === MOUNT_COURSE.mountExpress) {
      sendMountQuickSelect().then(() => {})
    }
  }

  const { data, isLoading, isError } = useEstimateQuery(params, enabled)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setErrors(data?.isError ? data.messages : {}), [data])

  // Save estimate result to context
  useEffect(() => {
    // 見積結果をコンテキストに保存
    if (data && !data.isError) {
      setEstimateSuccessResult(data)
    }

    // コースが実装短納期以外なら後続の処理を実施しない
    if (!data || data.isError || MountCourse.key !== MOUNT_COURSE.mountExpress.key) {
      return
    }
    const _result = parseResult(data)
    const isDisabled = shouldDisableMountExpress(
      _result.manufacture,
      _result.mount?.[manufactureCourse.key],
      manufactureCourse.key,
      !!_result.procurement,
    )

    if (isDisabled) {
      setManufactureMount(MOUNT_COURSE.mount)
      notify(`実装サービス : [${MOUNT_COURSE.mountExpress.name}] → [${MOUNT_COURSE.mount.name}]`)
    }
  }, [data, setEstimateSuccessResult, manufactureCourse.key, MountCourse.key, setManufactureMount])

  const preventer = usePreventUnload()
  const router = useNextRouter("/estimate/input-title")
  const routerIndividual = useNextRouter("/estimate/save/individual/form")
  const { startSaveProcess } = useContext(SaveEstimateContext)
  const { bomSnapshotId, deliveryZeroFlg } = useContext(OneClickEstimateContext)

  if (data === undefined || data.isError) {
    const estimateErrors = data?.isError ? data.messages : {}
    const errors = isError
      ? [
          // TODO 適切なエラーメッセージにする
          "サーバーエラーが発生しました。",
        ]
      : (Object.values(estimateErrors) as string[])

    return {
      data: null,
      errors,
      isLoading,
    }
  }

  const _result = parseResult(data)
  let total = _result.total[manufactureCourse.key]

  if (MountCourse.key === MOUNT_COURSE.mountExpress.key) {
    total = {
      isIndividual: true,
      days: null,
      amount: null,
      amountWithoutTax: null,
      arrivalDate: null,
      orderLimit: null,
      errors: null,
    }
  }

  const { manufacture, procurement } = _result
  const mount = _result.mount?.[manufactureCourse.key]

  const isMountExpressDisabled = shouldDisableMountExpress(manufacture, mount, manufactureCourse.key, !!procurement)

  const saveEstimate = async () => {
    const result = await loginApi.loginStatus()
    if (result.isError) {
      alert(result.data.message)
      return
    }

    const toSaveEstimatePage = () => {
      preventer.remove()
      const { values, meta } = params
      startSaveProcess({
        values,
        manufactureCourse,
        estimateSuccessResult: data,
        meta,
        bomSnapshotId,
        deliveryZeroFlg,
        mountQuickFlg: MountCourse === MOUNT_COURSE.mountExpress,
      })

      const { design } = _result
      const manufacture = _result.manufacture?.[manufactureCourse.key]
      const mount = _result.mount?.[manufactureCourse.key]
      if (
        design?.isIndividual ||
        manufacture?.isIndividual ||
        mount?.isIndividual ||
        MountCourse.key === MOUNT_COURSE.mountExpress.key
      ) {
        routerIndividual.push()
      } else {
        router.push()
      }
    }

    if (result.data.login) {
      toSaveEstimatePage()
    } else {
      openLoginModal({
        onMount() {
          preventer.add()
        },
        onLogin: toSaveEstimatePage,
        shouldRedirect: false,
      })
    }
  }

  const resultOnClick =
    deliveryZeroFlg && isDeliveryZeroAvailable(params.values, manufactureCourse) ? openDeliveryZeroModal : saveEstimate

  return {
    data: {
      design: _result.design,
      manufacture: _result.manufacture,
      mount: _result.mount?.[manufactureCourse.key],
      procurement: _result.procurement,
      discounts: _result.discounts?.[manufactureCourse.key],
      total,
    },
    isLoading,
    onChangeManufactureCourse,
    handleMountChange,
    selectedValue: manufactureCourse,
    selectedMountValue: MountCourse,
    isMountExpressDisabled,
    resultOnClick,
    saveEstimate,
    openEstimateDetail() {
      csComEstApi.print(_result.paramStringify, _result.resultStringify)
    },
  }
}

export default useEstimateResult
